<template>
	<div class="ReportMetaTools">
		
		<!-- Social share (visible to everyone for non-private trips) -->
		<CloButton
			v-if="privacySetting !== 'locked'"
			id="social-share-btn"
			icon="socialShare"
			size="sm"
			hollow
			reverse
			@click.native="showDialog"
			><span class="u-showForMedium">{{ $t('generic.share') }}</span></CloButton
		>
		<!-- Social share dialog -->
		<CloDialog v-show="dialogOpen" id="regions-dialog" @close="closeDialog">
			<template #header>
				<CloHeading level="4" size="2" class="u-stack-md">{{ $t('generic.share') }}</CloHeading>
			</template>
			<template #body>
				<div class="ReportMetaTools-shareOptions">
					<!-- Facebook -->
					<a
						class="Button Button--hollow"
						target="_blank"
						rel="noopener"
						:href="`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`"
					>
						<CloIcon symbol="socialFacebook"/>
						<span>{{ $t('generic.shareFacebook') }}</span>
					</a>
					<!-- Twitter -->
					<a
						class="Button Button--hollow"
						target="_blank"
						rel="noopener"
						:href="`https://twitter.com/intent/tweet?text=${shareUrl}`"
					>
						<CloIcon symbol="socialTwitter"/>
						<span>{{ $t('generic.shareTwitter') }}</span>
					</a>
					<!-- Email -->
					<a 
						class="Button Button--hollow" 
						:href="`mailto:?&amp;body=${shareUrl}`"
					>
						<CloIcon symbol="contact"/>
						<span>{{ $t('generic.shareEmail') }}</span>
					</a>
					<!-- Copy link -->
					<div class="ReportMetaTools-shareOptions-email">
						<CloInputText 
							:label="$t('generic.copy')"
							type="text"
							:readonly="true"
							:value="shareUrl"
						/>
						<button type="button" class="Button Button--hollow" @click="copyShareUrl">{{ $t('generic.copy') }}</button>
						<span v-if="copiedShareUrl" class="ReportMetaTools-shareOptions-email-confirmation u-color-success">
							<CloIcon symbol="check" size="sm"/>
							<span>{{ $t('mediaSearch.copied') }}</span>
						</span>
					</div>
				</div>
			</template>
			<template #footer>
				<button class="Button" @click="closeDialog">{{ $t('generic.done') }}</button>
			</template>
		</CloDialog>

		<!-- Edit (visible for owner, editor, or commenter) -->
		<div v-if="currentUser.isParticipant" class="Dropdown" tabindex="-1">
			<span class="Dropdown-heading" tabindex="-1">
				<CloButton id="edit-report-btn" icon="edit" hollow reverse dropdown size="sm"
					><span class="u-showForMedium">{{ $t('generic.Edit') }}</span></CloButton
				>
				<span class="Dropdown-closer" @click="$el.focus()"></span>
			</span>
			<div class="Dropdown-panel" tabindex="-1">
				<ul class="u-unlist">
					<!-- Edit definition (only owner and editor) -->
					<li v-if="currentUser.role === 'owner'">
						<a :href="`${baseUrl}tripreport/edit/${tripId}?view=definition`">{{ $t('tripReport.editDefinition') }}</a>
					</li>
					<!-- Manage checklists (all participants) -->
					<li>
						<a :href="`${baseUrl}tripreport/edit/${tripId}?view=checklists`">{{ $t('tripReport.manageChecklists') }}</a>
					</li>
					<!-- Manage people (only owner and editor) -->
					<li v-if="currentUser.role !== 'participant'">
						<a :href="`${baseUrl}tripreport/edit/${tripId}?view=people`">{{ hasInvitedPeople ? $t('tripReport.managePeople') : $t('tripReport.addPeople') }}</a>
					</li>
					<!-- Edit narrative (only owner and editor) -->
					<li v-if="currentUser.role !== 'participant'">
						<a :href="`${baseUrl}tripreport/edit/${tripId}?view=narrative`">{{ narrative ? $t('tripReport.narrativeEdit') : $t('tripReport.narrativeAdd') }}</a>
					</li>
					<!-- Add comment (all participants) -->
					<li>
						<a :href="`${baseUrl}tripreport/edit/${tripId}?view=comment`"
							>{{ hasCurrentUserComment ? $t('tripReport.commentEdit') : $t('tripReport.commentAdd') }}
						</a>
					</li>
					<!-- Download (all participants) -->
					<!-- <li>
						<a :href="`${baseUrl}tripreport/${tripId}?print=true`">{{ $t('generic.viewForPrintPdf') }}</a>
					</li> -->
					<!-- Delete (owner) or remove me (other participants) -->
					<li v-if="currentUser.role === 'owner'">
						<a @click="deleteReport">{{ $t('tripReport.delete') }}</a>
					</li>
					<li v-else>
						<a @click="removeSelf">{{ $t('tripReport.removeMe') }}</a>
					</li>
				</ul>
			</div>
		</div>

		<!-- Help icon/dialog -->
		<ReportHelp v-if="!currentUser.isParticipant" hollow/>

	</div>
</template>

<script>
import axios from 'axios';
import CloButton from '@/base/CloButton/CloButton.vue';
import CloDropdown from '@/base/CloDropdown/CloDropdown.vue';
import CloInputText from '@/forms/CloInputText/CloInputText.vue';
import ReportHelp from './ReportHelp.vue';

export default {
	name: 'ReportMetaTools',
	components: {
		CloButton,
		CloDropdown,
		CloInputText,
		ReportHelp,
	},
	props: {
		currentUser: Object,
		privacySetting: String,
		tripId: Number,
		comments: {
			type: Array,
			required: true,
		},
		narrative: String,
		hasInvitedPeople: Boolean,
	},
	data() {
		return {
			baseUrl: window.baseUrl,
			tripManageApiBaseUrl: window.tripManageApiBaseUrl,
			csrf: window['_csrf'],
			csrf_header: window['_csrf_header'],
			dialogOpen: false,
			dialogOpener: null,
			copiedShareUrl: false,
			shareUrl: window.location.href,
		};
	},
	methods: {
		showDialog(e) {
			this.dialogOpen = true;
			this.dialogOpener = e.target;
		},
		closeDialog() {
			this.dialogOpen = false;
			// important: put focus back on element that triggered the dialog
			if (this.dialogOpener) {
				this.dialogOpener.focus();
			}
			this.copiedShareUrl = false; // reset
		},
		deleteReport() {
			if (confirm(this.$t('tripReport.confirmDelete'))) {
				let url = this.tripManageApiBaseUrl + 'delete/' + this.tripId;
				let headers = {};
				headers[this.csrf_header] = this.csrf;
				axios
					.delete(url, {
						headers: headers,
						withCredentials: true,
					})
					.then((res) => {
						window.location = baseUrl + 'mytripreports';
					})
					.catch((err) => {
						console.log(err);
					});
			} else {
				// TODO how best to close the Edit panel. Should we do this before opening the confirm dialog?
			}
		},
		removeSelf() {
			if (confirm(this.$t('tripReport.confirmRemoveSelf'))) {
				let url = this.tripManageApiBaseUrl + 'decline-invite/' + this.tripId;
				let headers = {};
				headers[this.csrf_header] = this.csrf;
				axios
					.delete(url, {
						headers: headers,
						withCredentials: true,
					})
					.then((res) => {
						window.location = baseUrl + 'mytripreports';
					})
					.catch((err) => {
						console.log(err);
					});
			} else {
				// TODO how best to close the Edit panel. Should we do this before opening the confirm dialog?
			}
		},
		copyShareUrl() {
			navigator.clipboard.writeText(this.shareUrl);
			this.copiedShareUrl = true;
		}
	},
	computed: {
		hasCurrentUserComment() {
			if (this.comments.length > 0) {
				return this.comments.find((comment) => comment.tripReportPersonId === this.currentUser.tripReportPersonId);
			}
			return false;
		},
	},
};
</script>

<style lang="scss">
.ReportMetaTools {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: $space-xs;
	@include breakpoint(medium) {
		flex-direction: row;
		align-items: center;
		gap: $space-sm;
	}
	.Button {
		display: inline-flex;
		align-items: center;
		gap: $space-xs;
		margin: 0;
	}
	.Dropdown-heading {
		padding: 0;
	}
	.Dropdown-heading .Icon--triangleDown {
		margin: 0;
	}
	.Dropdown-panel {
		right: 0;
		[dir='rtl'] & {
			left: 0;
			right: auto;
		}
		@include breakpoint(medium) {
			left: 0;
			right: auto;
			[dir='rtl'] & {
				right: 0;
				left: auto;
			}
		}
	}
	.ReportMetaTools-shareOptions {
		display: flex;
		flex-wrap: wrap;
		gap: $space-md;
		@include stack-md;
		.ReportMetaTools-shareOptions-email {
			flex-basis: 100%;
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			gap: $space-sm;
			.InputText {
				margin: 0;
				flex-grow: 1;
			}
			.InputText-label {
				@include visuallyHidden;
			}
			.ReportMetaTools-shareOptions-email-confirmation {
				display: flex;
				gap: $space-xs;
				align-items: center;
				@include text(3);
				flex-basis: 100%;
			}
		}
	}
}
</style>
